/**
 * The screens were written against the admin's own `/api/*` addresses
 * (`fetch('/api/currencies')` → `{ currencies }`). Those addresses used to
 * be Next.js route handlers; here the same handlers run in the browser:
 * each turns the request into a call to the server's /api/v1/admin/* API
 * and shapes the answer the way the screen expects.
 *
 * `adminFetch` has `fetch`'s signature, so a screen changes one word.
 */

export type Handler = (req: Request, params: Record<string, string>) => Promise<Response>;
export type Route = [method: string, pattern: string, handler: Handler];

function matchPath(pattern: string, pathname: string): Record<string, string> | null {
  const want = pattern.split('/');
  const got = pathname.split('/');
  if (want.length !== got.length) return null;
  const params: Record<string, string> = {};
  for (let i = 0; i < want.length; i++) {
    if (want[i].startsWith(':')) params[want[i].slice(1)] = decodeURIComponent(got[i]);
    else if (want[i] !== got[i]) return null;
  }
  return params;
}

export function createAdminFetch(routes: Route[]) {
  return async function adminFetch(input: string, init: RequestInit = {}): Promise<Response> {
    const url = new URL(input, window.location.origin);
    const method = (init.method ?? 'GET').toUpperCase();

    let pathKnown = false;
    for (const [routeMethod, pattern, handler] of routes) {
      const params = matchPath(pattern, url.pathname);
      if (!params) continue;
      pathKnown = true;
      if (routeMethod !== method) continue;
      try {
        return await handler(new Request(url, { ...init, method }), params);
      } catch (err) {
        return Response.json({ error: err instanceof Error ? err.message : 'Request failed' }, { status: 500 });
      }
    }
    return Response.json(
      { error: pathKnown ? 'Method not allowed' : 'Not found' },
      { status: pathKnown ? 405 : 404 },
    );
  };
}
